<template>
  <div class="home-layout">
    <div class="home-content">
      <!-- 左侧导航栏 -->
      <Sidebar
        :is-collapsed="isCollapsed"
        :active-menu="activeMenu"
        @toggleCollapse="toggleCollapse"
        @menuSelect="handleMenuSelect"
      />
      <!-- 右侧内容区 -->
      <main
        class="main-content"
        :class="{
          shifted: isCollapsed,
          'project-template-mode': currentView === 'projectManage',
        }"
      >
        <section class="content">
          <LocalSpace v-if="currentView === 'local'" />
          <PersonalCenter v-else-if="currentView === 'center'" />
          <BackgroundTask v-else-if="currentView === 'task'" />
          <CardList v-else-if="currentView === 'card'"/>
          <AgentCenter v-else-if="currentView === 'agentCenter'" />
          <div v-else-if="currentView === 'projectManage'" class="project-template-stack">
            <ProjectManage embedded />
            <TemplateMng embedded />
          </div>
          <D07ProjectList v-else-if="currentView === 'd07'" />
          <D63ProjectList v-else-if="currentView === 'd63'" />
          <D64ProjectList v-else-if="currentView === 'd64'" />
          <TemplateMng v-else-if="currentView === 'TemplateMng'" />
          <MyTasks v-else-if="currentView === 'myTasks'" @task-select="handleTaskSelect" />
          <TaskDetail v-else-if="currentView === 'taskDetail'" :task-id="selectedTaskId" @back="handleTaskBack" />
        </section>
      </main>
    </div>
    
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Sidebar from '@/apps/portal/components/Sidebar.vue'
import defaultAvatar from '@/shared/assets/avatar.jpg'
import LocalSpace from '@/apps/portal/views/storage/LocalSpace.vue'
import PersonalCenter from '@/apps/portal/views/account/PersonalCenter.vue'
import CardList from '@/apps/portal/views/content-tools/CardList.vue'
import BackgroundTask from '@/apps/portal/views/background-task/BackgroundTask.vue'
import AgentCenter from '@/apps/portal/views/account/AgentCenter.vue'
import ProjectManage from '@/apps/portal/views/batch-production/auto-project/ProjectManage.vue'
import MyTasks from '@/apps/portal/views/batch-production/auto-project/MyTasks.vue'
import TaskDetail from '@/apps/portal/views/batch-production/auto-project/TaskDetail.vue'
import TemplateMng from '@/apps/portal/views/templates/TemplateMng.vue'
import D07ProjectList from '@/apps/portal/views/batch-production/video-mix/D07.vue'
import D63ProjectList from '@/apps/portal/views/batch-production/digital-human/D63.vue'
import D64ProjectList from '@/apps/portal/views/batch-production/fast-video-mix/D64.vue'

const route = useRoute()
const router = useRouter()

/** 侧边 index → 主内容区 view，与路由 query.view 一致，刷新后可恢复 */
const MENU_INDEX_TO_VIEW = {
  i_0: 'card',
  i_5: 'local',
  i_7: 'center',
  i_10: 'task',
  i_11: 'agentCenter',
  i_12: 'projectManage',
  i_13: 'myTasks',
  i_14: 'TemplateMng',
  i_16: 'd07',
  i_17: 'd63',
  i_18: 'd64',
}

const VIEW_TO_MENU_INDEX = Object.fromEntries(
  Object.entries(MENU_INDEX_TO_VIEW).map(([k, v]) => [v, k]),
)

const VALID_HOME_VIEWS = new Set([
  ...Object.values(MENU_INDEX_TO_VIEW),
  'taskDetail',
])

const isCollapsed = ref(false)
const activeMenu = ref('i_0')
const currentView = ref('card')
const selectedTaskId = ref(null)

function replaceHomeQuery(view, options = {}) {
  const next = { ...route.query }
  next.view = view
  if (view === 'taskDetail' && options.taskId != null) {
    next.taskId = String(options.taskId)
  } else {
    delete next.taskId
  }
  router.replace({ path: route.path, query: next })
}

function applyHomeViewFromRoute() {
  const raw = route.query.view
  const view = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : ''

  if (view === 'taskDetail') {
    const tidRaw = route.query.taskId
    const tid = typeof tidRaw === 'string' ? tidRaw : Array.isArray(tidRaw) ? tidRaw[0] : ''
    if (tid) {
      selectedTaskId.value = tid
      currentView.value = 'taskDetail'
      activeMenu.value = 'i_13'
    } else {
      selectedTaskId.value = null
      currentView.value = 'myTasks'
      activeMenu.value = 'i_13'
    }
    return
  }

  if (!view) {
    return
  }

  if (!VALID_HOME_VIEWS.has(view)) {
    selectedTaskId.value = null
    currentView.value = 'card'
    activeMenu.value = 'i_0'
    replaceHomeQuery('card')
    return
  }

  selectedTaskId.value = null
  currentView.value = view
  const menuIdx = VIEW_TO_MENU_INDEX[view]
  if (menuIdx) {
    activeMenu.value = menuIdx
  }
}

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
}

const handleMenuSelect = (index) => {
  activeMenu.value = index
  if (index === 'i_8') {
    // 邀请礼物功能已移除
    return
  }
  const view = MENU_INDEX_TO_VIEW[index]
  if (view) {
    currentView.value = view
    selectedTaskId.value = null
    replaceHomeQuery(view)
  } else {
    currentView.value = 'card'
    selectedTaskId.value = null
    replaceHomeQuery('card')
  }
}

const handleOpenPersonalCenter = () => {
  currentView.value = 'center'
  activeMenu.value = 'i_7'
  selectedTaskId.value = null
  replaceHomeQuery('center')
}

const handleTaskSelect = (task) => {
  selectedTaskId.value = task.id
  currentView.value = 'taskDetail'
  replaceHomeQuery('taskDetail', { taskId: task.id })
}

const handleTaskBack = () => {
  currentView.value = 'myTasks'
  selectedTaskId.value = null
  replaceHomeQuery('myTasks')
}

// 用户信息
const userName = ref('')
const userAvatar = ref(defaultAvatar)
const getUserInfo = () => {
  const userInfo = localStorage.getItem('userInfo')
  if (userInfo) {
    const parsedInfo = JSON.parse(userInfo)
    userName.value = parsedInfo.name || ''
    userAvatar.value = parsedInfo.headPortrait || defaultAvatar
  } else {
    userName.value = '小明'
    userAvatar.value = defaultAvatar
  }
}

watch(
  () => [route.query.view, route.query.taskId],
  () => {
    applyHomeViewFromRoute()
  },
  { immediate: true },
)

onMounted(() => {
  getUserInfo()
})

</script>

<style scoped>
.home-layout {
  height: 100vh;
  width: 100%;
  background-color: var(--bg-app);
  /* 背景氛围光斑 - 增加生命力 */
  background-image: 
    radial-gradient(circle at 10% 20%, rgba(147, 51, 234, 0.1) 0%, transparent 40%),
    radial-gradient(circle at 90% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.home-content {
  position: relative;
  width: 100%;
  height: calc(100vh - 50px);
  display: flex;
  padding: 20px;
  gap: 20px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
  border: 1px solid rgba(255, 255, 255, 0.5);
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
}

.main-content.project-template-mode {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.content {
  flex: 1;
  display: flex;
  padding: 0;
  margin-top: 0;
  overflow: hidden;
  position: relative;
  min-height: 0;
}

.project-template-stack {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 2fr) minmax(0, 1fr);
  gap: 20px;
  overflow: hidden;
  background: transparent;
  box-sizing: border-box;
}

</style>
